import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { CallCenterSearchResult } from 'call-center/models/CallCenterSearchResult'
import { filterCallCenterMemberships } from 'call-center/utils'
import WorkspaceMembershipStore, {
  Membership,
} from 'iam/WorkspaceMembershipStore'
import Member from 'iam/models/Member'
import Workspace from 'iam/models/Workspace'
import WorkspaceStore from 'iam/WorkspaceStore'
import { Id } from 'types/definitions'

export interface CallCenterMembershipItem {
  key: string
  workspaceId: string
  workspace: Workspace | null
  member: Member | null
  type: 'membership' | 'waitlist'
  isWaitlistWorkspace: boolean
  loading: boolean
  error: string | null
}

export class CallCenterSelectionViewModel {
  @observable
  selectedResult: CallCenterSearchResult | null = null

  @observable.ref
  selectedMemberships: Membership[] = []

  constructor(
    private workspaceMembershipStore: WorkspaceMembershipStore,
    private workspaceStore: WorkspaceStore,
  ) {
    makeObservable(this)
  }

  @computed
  get hasSelection() {
    return this.selectedResult !== null
  }

  @computed
  get hasWaitlistSignup() {
    return this.selectedResult?.hubspotContact?.signedUpForWaitlist ?? false
  }

  @computed
  get waitlistWorkspaceId() {
    return this.selectedResult?.hubspotContact?.waitlistWorkspaceId ?? null
  }

  @computed
  get membershipItems(): CallCenterMembershipItem[] {
    const waitlistWorkspaceId = this.waitlistWorkspaceId
    const hasWaitlistSignup = this.hasWaitlistSignup

    const items: CallCenterMembershipItem[] = this.selectedMemberships.map(
      (membership) => {
        const workspaceId = Id.assert(membership.workspace.id)
        const userPublicId = Id.assert(membership.member.user.publicId)

        return {
          key: `membership:${workspaceId}:${userPublicId}`,
          workspaceId,
          workspace: membership.workspace,
          member: membership.member,
          type: 'membership' as const,
          isWaitlistWorkspace:
            hasWaitlistSignup && workspaceId === waitlistWorkspaceId,
          loading: false,
          error: null,
        }
      },
    )

    if (hasWaitlistSignup && waitlistWorkspaceId) {
      const alreadyListed = items.some(
        (item) => item.workspaceId === waitlistWorkspaceId,
      )

      if (!alreadyListed) {
        items.push({
          key: `waitlist:${waitlistWorkspaceId}`,
          workspaceId: waitlistWorkspaceId,
          workspace: this.loadWaitlistWorkspace.result ?? null,
          member: null,
          type: 'waitlist',
          isWaitlistWorkspace: true,
          loading: this.loadWaitlistWorkspace.pending,
          error: this.loadWaitlistWorkspace.error
            ? (this.loadWaitlistWorkspace.error as Error).message
            : null,
        })
      }
    }

    return items
  }

  @action.bound
  setSelectedResult(
    result: CallCenterSearchResult,
    initialMemberships: Membership[],
  ) {
    this.selectedResult = result
    this.selectedMemberships = initialMemberships
    this.updateWaitlistWorkspaceState()
  }

  @action.bound
  private updateWaitlistWorkspaceState() {
    const hasWaitlistSignup = this.hasWaitlistSignup
    const waitlistWorkspaceId = this.waitlistWorkspaceId

    if (!hasWaitlistSignup || !waitlistWorkspaceId) {
      return
    }

    const membership = this.selectedMemberships.find(
      (item) => item.workspace.id === waitlistWorkspaceId,
    )

    if (membership) {
      return
    }

    // Only load if not already done fetching.
    if (
      this.loadWaitlistWorkspace.pending ||
      this.loadWaitlistWorkspace.result?.id === waitlistWorkspaceId
    ) {
      return
    }

    void this.loadWaitlistWorkspace(waitlistWorkspaceId)
  }

  @action.bound
  clear() {
    this.selectedResult = null
    this.selectedMemberships = []
  }

  loadWaitlistWorkspace = task.resolved(async (workspaceId: string) => {
    const workspace = await this.workspaceStore.fetch(Id.assert(workspaceId))

    if (!workspace) {
      throw new Error('Unable to load workspace details.')
    }

    return workspace
  })

  async loadMembershipDetails(userPublicIds: Id[]) {
    const membershipArrays = await Promise.all(
      userPublicIds.map((id) =>
        this.workspaceMembershipStore.getUserWorkspaceMemberships(id),
      ),
    )

    const allMemberships = membershipArrays.flat()
    const filteredMemberships = filterCallCenterMemberships(allMemberships)

    const seen = new Set<string>()
    const dedupedMemberships = filteredMemberships.filter((m) => {
      const key = `${m.workspace.id}:${m.member.user.publicId}`
      if (seen.has(key)) {
        return false
      }
      seen.add(key)
      return true
    })

    runInAction(() => {
      this.selectedMemberships = dedupedMemberships
      this.updateWaitlistWorkspaceState()
    })
  }
}
